// CMO IA Dashboard - Agent d'exécution Marketing
// OmniHub IA - Génère, publie, vérifie, ajuste

import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Megaphone,
  FileText,
  Calendar,
  Users,
  BarChart3,
  Sparkles,
  Send,
  RefreshCw,
  Plus,
  Eye,
  Edit,
  Trash2,
  CheckCircle2,
  Clock,
  AlertCircle,
  Loader2,
  Target,
  TrendingUp,
  Linkedin,
  Facebook,
  Globe,
  Mail,
  Search,
  ArrowRight,
  Zap,
  Cloud,
  CloudOff,
  Play
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem("session_token");
  return { Authorization: `Bearer ${token}` };
};

// Icônes par canal
const CanalIcon = ({ canal, className = "w-4 h-4" }) => {
  switch (canal) {
    case "linkedin": return <Linkedin className={className} />;
    case "facebook": return <Facebook className={className} />;
    case "blog": return <Globe className={className} />;
    case "email": return <Mail className={className} />;
    case "seo": return <Search className={className} />;
    default: return <FileText className={className} />;
  }
};

// Badge de statut
const StatusBadge = ({ status }) => {
  const configs = {
    draft: { label: "Brouillon", className: "bg-zinc-500/20 text-zinc-400", icon: Edit },
    generated: { label: "Généré", className: "bg-blue-500/20 text-blue-400", icon: Sparkles },
    review: { label: "À valider", className: "bg-amber-500/20 text-amber-400", icon: Eye },
    approved: { label: "Validé", className: "bg-emerald-500/20 text-emerald-400", icon: CheckCircle2 },
    scheduled: { label: "Planifié", className: "bg-purple-500/20 text-purple-400", icon: Clock },
    published: { label: "Publié", className: "bg-green-500/20 text-green-400", icon: Send },
    failed: { label: "Échec", className: "bg-red-500/20 text-red-400", icon: AlertCircle },
  };
  const config = configs[status] || configs.draft;
  const Icon = config.icon;
  return (
    <Badge className={config.className}>
      <Icon className="w-3 h-3 mr-1" />
      {config.label}
    </Badge>
  );
};

// Stats Cards
const StatsCards = ({ stats }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
    <Card className="bg-zinc-900/50 border-white/10">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-zinc-500">Contenus</p>
            <p className="text-2xl font-bold text-white">{stats.total_contents || 0}</p>
          </div>
          <FileText className="w-8 h-8 text-blue-400/50" />
        </div>
        <div className="flex gap-2 mt-1 text-xs">
          <span className="text-amber-400">{stats.drafts || 0} brouillons</span>
          <span className="text-green-400">{stats.published || 0} publiés</span>
        </div>
      </CardContent>
    </Card>
    
    <Card className="bg-zinc-900/50 border-white/10">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-zinc-500">Leads</p>
            <p className="text-2xl font-bold text-emerald-400">{stats.total_leads || 0}</p>
          </div>
          <Users className="w-8 h-8 text-emerald-400/50" />
        </div>
        <p className="text-xs text-zinc-500 mt-1">
          Ce mois: {stats.leads_this_month || 0}
        </p>
      </CardContent>
    </Card>
    
    <Card className="bg-zinc-900/50 border-white/10">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-zinc-500">Planning</p>
            <p className="text-2xl font-bold text-purple-400">{stats.planning_items || 0}</p>
          </div>
          <Calendar className="w-8 h-8 text-purple-400/50" />
        </div>
        <p className="text-xs text-zinc-500 mt-1">
          À venir: {stats.upcoming_publications || 0}
        </p>
      </CardContent>
    </Card>
    
    <Card className="bg-zinc-900/50 border-white/10">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-zinc-500">Performance</p>
            <p className="text-2xl font-bold text-white">{stats.total_views || 0}</p>
          </div>
          <TrendingUp className="w-8 h-8 text-amber-400/50" />
        </div>
        <p className="text-xs text-zinc-500 mt-1">
          Clics: {stats.total_clicks || 0}
        </p>
      </CardContent>
    </Card>
  </div>
);

// Content Card
const ContentCard = ({ content, onView, onPublish, onDelete }) => (
  <Card className="bg-zinc-900/50 border-white/10 hover:border-white/20 transition-colors">
    <CardContent className="p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500/20 to-indigo-500/20 flex items-center justify-center">
            <CanalIcon canal={content.canal} className="w-4 h-4 text-blue-400" />
          </div>
          <div>
            <p className="text-xs text-zinc-500 uppercase">{content.verticale}</p>
            <p className="text-xs text-zinc-400">{content.canal}</p>
          </div>
        </div>
        <StatusBadge status={content.status} />
      </div>
      
      <h3 className="text-white font-medium mb-2 line-clamp-2">{content.title}</h3>
      
      {content.excerpt && (
        <p className="text-sm text-zinc-400 line-clamp-2 mb-3">{content.excerpt}</p>
      )}
      
      <div className="flex items-center justify-between text-xs text-zinc-500">
        <span>{new Date(content.created_at).toLocaleDateString('fr-FR')}</span>
        <div className="flex items-center gap-2">
          {content.leads_generated > 0 && (
            <span className="text-emerald-400">{content.leads_generated} leads</span>
          )}
          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => onView(content)}>
            <Eye className="w-3 h-3" />
          </Button>
          {(content.status === 'generated' || content.status === 'approved') && (
            <Button size="sm" variant="ghost" className="h-7 px-2 text-green-400" onClick={() => onPublish(content)}>
              <Send className="w-3 h-3" />
            </Button>
          )}
          <Button 
            size="sm" 
            variant="ghost" 
            className="h-7 px-2 text-red-400 hover:text-red-300 hover:bg-red-500/10" 
            onClick={() => onDelete(content)}
            data-testid={`delete-content-${content.id}`}
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Generate Dialog
const GenerateDialog = ({ open, onClose, onGenerate, verticales }) => {
  const [verticale, setVerticale] = useState("");
  const [canal, setCanal] = useState("");
  const [contentType, setContentType] = useState("");
  const [brief, setBrief] = useState("");
  const [loading, setLoading] = useState(false);
  
  const handleGenerate = async () => {
    if (!verticale || !canal || !contentType) return;
    
    setLoading(true);
    try {
      await onGenerate({ verticale, canal, content_type: contentType, brief });
      onClose();
      setVerticale("");
      setCanal("");
      setContentType("");
      setBrief("");
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-white/10 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-400" />
            Générer du contenu (CMO IA)
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm text-zinc-400 mb-2 block">Verticale</label>
            <Select value={verticale} onValueChange={setVerticale}>
              <SelectTrigger className="bg-zinc-800 border-white/10 text-white">
                <SelectValue placeholder="Choisir une verticale" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-white/10">
                {verticales.map(v => (
                  <SelectItem key={v.code} value={v.code} className="text-white">
                    {v.nom}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="text-sm text-zinc-400 mb-2 block">Canal</label>
            <Select value={canal} onValueChange={setCanal}>
              <SelectTrigger className="bg-zinc-800 border-white/10 text-white">
                <SelectValue placeholder="Choisir un canal" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-white/10">
                <SelectItem value="blog" className="text-white">Blog / WordPress</SelectItem>
                <SelectItem value="linkedin" className="text-white">LinkedIn</SelectItem>
                <SelectItem value="facebook" className="text-white">Facebook</SelectItem>
                <SelectItem value="seo" className="text-white">Page SEO</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="text-sm text-zinc-400 mb-2 block">Type de contenu</label>
            <Select value={contentType} onValueChange={setContentType}>
              <SelectTrigger className="bg-zinc-800 border-white/10 text-white">
                <SelectValue placeholder="Choisir un type" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-white/10">
                <SelectItem value="article" className="text-white">Article de blog</SelectItem>
                <SelectItem value="post_social" className="text-white">Post social</SelectItem>
                <SelectItem value="page_seo" className="text-white">Page SEO</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="text-sm text-zinc-400 mb-2 block">Brief (optionnel)</label>
            <Textarea
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              placeholder="Instructions spécifiques pour le CMO IA..."
              className="bg-zinc-800 border-white/10 text-white min-h-[80px]"
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button 
            onClick={handleGenerate} 
            disabled={!verticale || !canal || !contentType || loading}
            className="bg-gradient-to-r from-amber-500 to-orange-500"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            Générer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// View Content Dialog
const ViewContentDialog = ({ content, open, onClose }) => {
  if (!content) return null;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-white/10 max-w-2xl max-h-[80vh] overflow-y-auto pr-6">
        <DialogHeader>
          <DialogTitle className="text-white">{content.title}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className="bg-zinc-800">{content.verticale}</Badge>
            <Badge className="bg-zinc-800">
              <CanalIcon canal={content.canal} className="w-3 h-3 mr-1" />
              {content.canal}
            </Badge>
            <StatusBadge status={content.status} />
          </div>
          
          {content.meta_description && (
            <div className="p-3 bg-zinc-800/50 rounded-lg">
              <p className="text-xs text-zinc-500 mb-1">Meta description</p>
              <p className="text-sm text-zinc-300">{content.meta_description}</p>
            </div>
          )}
          
          <div className="prose prose-invert max-w-none">
            <div className="text-zinc-300 whitespace-pre-wrap">{content.body}</div>
          </div>
          
          {content.keywords?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {content.keywords.map((kw, i) => (
                <Badge key={i} variant="outline" className="text-xs">{kw}</Badge>
              ))}
            </div>
          )}
          
          {content.zoho_path && (
            <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
              <p className="text-xs text-blue-400">
                <Cloud className="w-3 h-3 inline mr-1" />
                Zoho: {content.zoho_path}
                {!content.zoho_uploaded && " (dry-run)"}
              </p>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Component
export default function CMODashboard() {
  const [stats, setStats] = useState({});
  const [contents, setContents] = useState([]);
  const [verticales, setVerticales] = useState([]);
  const [leads, setLeads] = useState([]);
  const [zohoStatus, setZohoStatus] = useState({});
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");
  
  // Filters
  const [verticaleFilter, setVerticaleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Dialogs
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [viewContent, setViewContent] = useState(null);
  
  const loadData = useCallback(async () => {
    try {
      const [statsRes, contentsRes, verticalesRes, leadsRes, zohoRes] = await Promise.all([
        axios.get(`${API}/api/marketing/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/marketing/contents`, { 
          headers: getAuthHeaders(),
          params: {
            verticale: verticaleFilter !== "all" ? verticaleFilter : undefined,
            status: statusFilter !== "all" ? statusFilter : undefined,
            limit: 50
          }
        }),
        axios.get(`${API}/api/marketing/verticales`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/marketing/leads`, { headers: getAuthHeaders(), params: { limit: 20 } }),
        axios.get(`${API}/api/marketing/zoho/status`, { headers: getAuthHeaders() })
      ]);
      
      setStats(statsRes.data || {});
      setContents(contentsRes.data.contents || []);
      setVerticales(verticalesRes.data.verticales || []);
      setLeads(leadsRes.data.leads || []);
      setZohoStatus(zohoRes.data || {});
    } catch (err) {
      console.error("Erreur chargement Marketing Hub:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [verticaleFilter, statusFilter]);
  
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  const handleRefresh = () => {
    setRefreshing(true);
    loadData();
  };
  
  const handleGenerate = async (data) => {
    try {
      const res = await axios.post(`${API}/api/marketing/generate`, data, {
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" }
      });
      if (res.data.success) {
        loadData();
      }
      return res.data;
    } catch (err) {
      console.error("Erreur génération:", err);
      throw err;
    }
  };
  
  const handlePublish = async (content) => {
    const url = prompt("URL de publication:");
    if (!url) return;
    
    try {
      await axios.post(
        `${API}/api/marketing/contents/${content.id}/publish`,
        null,
        { headers: getAuthHeaders(), params: { published_url: url } }
      );
      loadData();
    } catch (err) {
      console.error("Erreur publication:", err);
    }
  };
  
  // Supprimer un contenu
  const handleDeleteContent = async (content) => {
    if (!confirm(`Supprimer définitivement "${content.title}" ?`)) return;
    
    try {
      await axios.delete(`${API}/api/marketing/contents/${content.id}`, {
        headers: getAuthHeaders()
      });
      loadData();
    } catch (err) {
      console.error("Erreur suppression:", err);
      alert("Erreur lors de la suppression");
    }
  };
  
  // Supprimer une verticale
  const handleDeleteVerticale = async (verticale) => {
    if (!confirm(`Supprimer la verticale "${verticale.nom}" ? Cette action est irréversible.`)) return;
    
    try {
      await axios.delete(`${API}/api/marketing/verticales/${verticale.code}`, {
        headers: getAuthHeaders()
      });
      loadData();
    } catch (err) {
      console.error("Erreur suppression verticale:", err);
      alert("Erreur lors de la suppression");
    }
  };
  
  // Toggle activation verticale
  const handleToggleVerticale = async (verticale) => {
    try {
      await axios.put(`${API}/api/marketing/verticales/${verticale.code}`, {
        ...verticale,
        enabled: !verticale.enabled
      }, {
        headers: getAuthHeaders()
      });
      loadData();
    } catch (err) {
      console.error("Erreur modification verticale:", err);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
            <Megaphone className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Plan d&apos;exécution CMO IA</h2>
            <p className="text-sm text-zinc-400">Exécution des décisions Marketing validées</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
          <Button 
            size="sm"
            className="bg-gradient-to-r from-amber-500 to-orange-500"
            onClick={() => setShowGenerateDialog(true)}
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Exécuter la décision
          </Button>
        </div>
      </div>
      
      {/* Zoho Status Banner */}
      <Card className={`border-white/10 ${zohoStatus.is_dry_run ? 'bg-amber-500/10' : 'bg-emerald-500/10'}`}>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            {zohoStatus.is_dry_run ? (
              <CloudOff className="w-5 h-5 text-amber-400" />
            ) : (
              <Cloud className="w-5 h-5 text-emerald-400" />
            )}
            <div className="flex-1">
              <p className="text-white font-medium">
                Zoho WorkDrive: {zohoStatus.is_dry_run ? "Mode Dry-Run" : "Connecté"}
              </p>
              <p className="text-xs text-zinc-400">{zohoStatus.message}</p>
            </div>
            {zohoStatus.is_dry_run && (
              <Badge className="bg-amber-500/20 text-amber-400">Simulation</Badge>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Stats */}
      <StatsCards stats={stats} />
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-900 border border-white/10">
          <TabsTrigger value="dashboard" className="data-[state=active]:bg-zinc-800">
            <BarChart3 className="w-4 h-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="contents" className="data-[state=active]:bg-zinc-800">
            <FileText className="w-4 h-4 mr-2" />
            Contenus
          </TabsTrigger>
          <TabsTrigger value="verticales" className="data-[state=active]:bg-zinc-800">
            <Target className="w-4 h-4 mr-2" />
            Verticales
          </TabsTrigger>
          <TabsTrigger value="leads" className="data-[state=active]:bg-zinc-800">
            <Users className="w-4 h-4 mr-2" />
            Leads
          </TabsTrigger>
        </TabsList>
        
        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="mt-4">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Quick Actions */}
            <Card className="bg-zinc-900/50 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-400" />
                  Actions rapides
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => setShowGenerateDialog(true)}
                >
                  <Sparkles className="w-4 h-4 mr-2 text-amber-400" />
                  Générer un article de blog
                  <ArrowRight className="w-4 h-4 ml-auto" />
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => setShowGenerateDialog(true)}
                >
                  <Linkedin className="w-4 h-4 mr-2 text-blue-400" />
                  Créer un post LinkedIn
                  <ArrowRight className="w-4 h-4 ml-auto" />
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => setShowGenerateDialog(true)}
                >
                  <Facebook className="w-4 h-4 mr-2 text-indigo-400" />
                  Créer un post Facebook
                  <ArrowRight className="w-4 h-4 ml-auto" />
                </Button>
              </CardContent>
            </Card>
            
            {/* Verticales Overview */}
            <Card className="bg-zinc-900/50 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Target className="w-5 h-5 text-purple-400" />
                  Verticales actives
                </CardTitle>
              </CardHeader>
              <CardContent>
                {verticales.length === 0 ? (
                  <p className="text-zinc-500 text-sm">Aucune verticale configurée</p>
                ) : (
                  <div className="space-y-3">
                    {verticales.map(v => (
                      <div key={v.id} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                        <div>
                          <p className="text-white font-medium">{v.nom}</p>
                          <p className="text-xs text-zinc-500">{v.keywords?.slice(0, 3).join(", ")}</p>
                        </div>
                        <div className="flex gap-1">
                          {v.canaux_actifs?.map(c => (
                            <div key={c} className="w-6 h-6 rounded bg-zinc-700 flex items-center justify-center">
                              <CanalIcon canal={c} className="w-3 h-3 text-zinc-400" />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          
          {/* Recent Contents */}
          {contents.length > 0 && (
            <Card className="bg-zinc-900/50 border-white/10 mt-6">
              <CardHeader className="flex flex-col gap-2 md:flex-row md:gap-0 items-center justify-between">
                <CardTitle className="text-white">Derniers contenus</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setActiveTab("contents")}>
                  Voir tout <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {contents.slice(0, 6).map(content => (
                    <ContentCard
                      key={content.id}
                      content={content}
                      onView={setViewContent}
                      onPublish={handlePublish}
                      onDelete={handleDeleteContent}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        {/* Contents Tab */}
        <TabsContent value="contents" className="mt-4">
          <div className="flex items-center gap-3 mb-4">
            <Select value={verticaleFilter} onValueChange={setVerticaleFilter}>
              <SelectTrigger className="w-40 bg-zinc-900 border-white/10 text-white">
                <SelectValue placeholder="Verticale" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-white/10">
                <SelectItem value="all" className="text-white">Toutes</SelectItem>
                {verticales.map(v => (
                  <SelectItem key={v.code} value={v.code} className="text-white">{v.nom}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 bg-zinc-900 border-white/10 text-white">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-white/10">
                <SelectItem value="all" className="text-white">Tous</SelectItem>
                <SelectItem value="generated" className="text-white">Générés</SelectItem>
                <SelectItem value="published" className="text-white">Publiés</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {contents.length === 0 ? (
            <Card className="bg-zinc-900/50 border-white/10">
              <CardContent className="p-8 text-center">
                <FileText className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                <p className="text-white font-medium">Aucun contenu</p>
                <Button className="mt-4" onClick={() => setShowGenerateDialog(true)}>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Générer du contenu
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {contents.map(content => (
                <ContentCard
                  key={content.id}
                  content={content}
                  onView={setViewContent}
                  onPublish={handlePublish}
                  onDelete={handleDeleteContent}
                />
              ))}
            </div>
          )}
        </TabsContent>
        
        {/* Verticales Tab */}
        <TabsContent value="verticales" className="mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            {verticales.map(v => (
              <Card key={v.id} className="bg-zinc-900/50 border-white/10">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-white">{v.nom}</h3>
                      <p className="text-sm text-zinc-400">{v.description}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={v.enabled ? "bg-emerald-500/20 text-emerald-400" : "bg-zinc-500/20"}>
                        {v.enabled ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-zinc-500 mb-1">Mots-clés SEO</p>
                      <div className="flex flex-wrap gap-1">
                        {v.keywords?.map((kw, i) => (
                          <Badge key={i} variant="outline" className="text-xs">{kw}</Badge>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <p className="text-xs text-zinc-500 mb-1">Canaux</p>
                      <div className="flex gap-2">
                        {v.canaux_actifs?.map(c => (
                          <div key={c} className="flex items-center gap-1 px-2 py-1 bg-zinc-800 rounded">
                            <CanalIcon canal={c} className="w-3 h-3" />
                            <span className="text-xs text-zinc-400">{c}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Actions verticale */}
                    <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/5">
                      <Button
                        size="sm"
                        variant="ghost"
                        className={v.enabled ? "text-amber-400 hover:bg-amber-500/10" : "text-emerald-400 hover:bg-emerald-500/10"}
                        onClick={() => handleToggleVerticale(v)}
                        data-testid={`toggle-verticale-${v.code}`}
                      >
                        {v.enabled ? "Désactiver" : "Activer"}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-400 hover:bg-red-500/10"
                        onClick={() => handleDeleteVerticale(v)}
                        data-testid={`delete-verticale-${v.code}`}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Supprimer
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
        
        {/* Leads Tab */}
        <TabsContent value="leads" className="mt-4">
          {leads.length === 0 ? (
            <Card className="bg-zinc-900/50 border-white/10">
              <CardContent className="p-8 text-center">
                <Users className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                <p className="text-white font-medium">Aucun lead</p>
                <p className="text-sm text-zinc-500">Les leads apparaîtront ici</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {leads.map(lead => (
                <Card key={lead.id} className="bg-zinc-900/50 border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                          <Users className="w-5 h-5 text-emerald-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{lead.prenom} {lead.nom}</p>
                          <p className="text-xs text-zinc-500">{lead.email}</p>
                        </div>
                      </div>
                      <Badge className="bg-zinc-800">{lead.verticale}</Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
      
      {/* Dialogs */}
      <GenerateDialog
        open={showGenerateDialog}
        onClose={() => setShowGenerateDialog(false)}
        onGenerate={handleGenerate}
        verticales={verticales}
      />
      
      <ViewContentDialog
        content={viewContent}
        open={!!viewContent}
        onClose={() => setViewContent(null)}
      />
    </div>
  );
}
